// form-data-persistence.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface FormDataState {
  formValue: any;
  formDataBackup: any;
  currentStep: number;
  stepsCompleted: boolean[];
  timestamp: number;
  formId: string;
}

@Injectable({
  providedIn: 'root'
})
export class FormDataPersistenceService {
  private formDataMap = new Map<string, FormDataState>();
  private currentFormId: string | null = null;
  private formDataSubject = new BehaviorSubject<FormDataState | null>(null);
  
  // Configuration
  private readonly STORAGE_PREFIX = 'form_data_';
  private readonly SESSION_STORAGE_KEY = 'active_form_id';
  private readonly EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes

  constructor() {
    // Check for existing form data on service initialization
    this.checkForExistingFormData();
    
    // Handle page unload to save data
    window.addEventListener('beforeunload', () => {
      if (this.currentFormId) {
        this.saveToSessionStorage();
      }
    });
  }

  /**
   * Initialize form data tracking
   */
  initializeForm(formId: string, initialData?: any): void {
    this.currentFormId = formId;
    
    // Check if we have existing data for this form
    const existingData = this.getFormData(formId);
    
    if (existingData && this.isDataValid(existingData)) {
      this.formDataSubject.next(existingData);
    } else {
      // Create new form state
      const newState: FormDataState = {
        formValue: initialData || {},
        formDataBackup: {},
        currentStep: 0,
        stepsCompleted: [],
        timestamp: Date.now(),
        formId: formId
      };
      this.formDataMap.set(formId, newState);
      this.formDataSubject.next(newState);
    }
    
    // Save current form ID to session storage
    sessionStorage.setItem(this.SESSION_STORAGE_KEY, formId);
  }

  /**
   * Save form data
   */
  saveFormData(formData: any, backupData?: any, currentStep?: number, stepsCompleted?: boolean[]): void {
    if (!this.currentFormId) return;
    
    const existingState = this.formDataMap.get(this.currentFormId);
    const updatedState: FormDataState = {
      formValue: formData || existingState?.formValue || {},
      formDataBackup: backupData || existingState?.formDataBackup || {},
      currentStep: currentStep !== undefined ? currentStep : (existingState?.currentStep || 0),
      stepsCompleted: stepsCompleted || existingState?.stepsCompleted || [],
      timestamp: Date.now(),
      formId: this.currentFormId
    };
    
    this.formDataMap.set(this.currentFormId, updatedState);
    this.formDataSubject.next(updatedState);
    
    // Auto-save to session storage
    this.saveToSessionStorage();
  }

  /**
   * Get current form data
   */
  getCurrentFormData(): FormDataState | null {
    if (!this.currentFormId) return null;
    return this.formDataMap.get(this.currentFormId) || null;
  }

  /**
   * Get form data by ID
   */
  getFormData(formId: string): FormDataState | null {
    // Check memory first
    if (this.formDataMap.has(formId)) {
      const data = this.formDataMap.get(formId);
      if (this.isDataValid(data)) {
        return data;
      }
    }
    
    // Check session storage
    const storageKey = this.STORAGE_PREFIX + formId;
    const storedData = sessionStorage.getItem(storageKey);
    if (storedData) {
      try {
        const parsedData: FormDataState = JSON.parse(storedData);
        if (this.isDataValid(parsedData)) {
          this.formDataMap.set(formId, parsedData);
          return parsedData;
        } else {
          // Remove expired data
          sessionStorage.removeItem(storageKey);
        }
      } catch (e) {
        console.error('Failed to parse stored form data', e);
      }
    }
    
    return null;
  }

  /**
   * Check if form data is valid (not expired)
   */
  private isDataValid(data: FormDataState | undefined): boolean {
    if (!data) return false;
    return (Date.now() - data.timestamp) < this.EXPIRY_TIME;
  }

  /**
   * Save current form data to session storage
   */
  private saveToSessionStorage(): void {
    if (!this.currentFormId) return;
    
    const data = this.formDataMap.get(this.currentFormId);
    if (data) {
      const storageKey = this.STORAGE_PREFIX + this.currentFormId;
      sessionStorage.setItem(storageKey, JSON.stringify(data));
    }
  }

  /**
   * Check for existing form data on page load
   */
  private checkForExistingFormData(): void {
    const savedFormId = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
    if (savedFormId) {
      const savedData = this.getFormData(savedFormId);
      if (savedData) {
        this.currentFormId = savedFormId;
        this.formDataSubject.next(savedData);
      }
    }
  }

  /**
   * Clear form data
   */
  clearFormData(formId?: string): void {
    const idToClear = formId || this.currentFormId;
    if (idToClear) {
      this.formDataMap.delete(idToClear);
      sessionStorage.removeItem(this.STORAGE_PREFIX + idToClear);
      
      if (idToClear === this.currentFormId) {
        this.currentFormId = null;
        sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
        this.formDataSubject.next(null);
      }
    }
  }

  /**
   * Clear all form data
   */
  clearAllFormData(): void {
    this.formDataMap.clear();
    sessionStorage.clear();
    this.currentFormId = null;
    this.formDataSubject.next(null);
  }

  /**
   * Get form data observable
   */
  getFormDataObservable(): Observable<FormDataState | null> {
    return this.formDataSubject.asObservable();
  }

  /**
   * Check if form has unsaved data
   */
  hasUnsavedData(): boolean {
    if (!this.currentFormId) return false;
    const data = this.formDataMap.get(this.currentFormId);
    return data !== undefined && Object.keys(data.formValue).length > 0;
  }

  /**
   * Generate unique form ID
   */
  static generateFormId(): string {
    return `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}